import React, { useState, useEffect } from 'react';
import { api, useAuth } from '@/contexts/AuthContext';
import { formatDate, formatINRFull, formatStatus } from '@/lib/utils';
import { Plus, Search, User, Phone, ChevronRight, ArrowLeft, Edit2, Check, X, Camera, Trash2 } from 'lucide-react';
import FileUpload from '@/components/FileUpload';
import { Pagination } from '@/components/Pagination';

const EMP_TYPES = ['TECHNICIAN', 'ENGINEER', 'SUPERVISOR', 'HELPER', 'DRIVER', 'OFFICE_STAFF'];
const PAGE_SIZE = 50;

export default function EmployeesPage() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const fetchEmployees = (p = page) => {
    const params = new URLSearchParams();
    if (filterType) params.set('employee_type', filterType);
    if (search) params.set('search', search);
    params.set('skip', ((p - 1) * PAGE_SIZE).toString());
    params.set('limit', PAGE_SIZE.toString());
    api.get(`/employees?${params}`).then(r => { setEmployees(r.data.employees || []); setTotalCount(r.data.total_count || 0); }).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchEmployees(page); }, [filterType, search, page]);

  const canCreate = ['DIRECTOR'].includes(user?.role);

  const viewEmployee = async (empId) => {
    try {
      const res = await api.get(`/employees/${empId}`);
      setSelectedEmp(res.data.employee);
    } catch (err) { alert('Failed to load employee'); }
  };

  if (selectedEmp) {
    return <EmployeeDetail emp={selectedEmp} onBack={() => setSelectedEmp(null)} onRefresh={() => viewEmployee(selectedEmp.id)} />;
  }

  return (
    <div data-testid="employees-page" className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 font-heading tracking-tight">Employees</h1>
          <p className="text-sm text-slate-500">{totalCount} employees</p>
        </div>
        {canCreate && (
          <button onClick={() => setShowCreate(true)} data-testid="add-employee-btn"
            className="bg-[#1B5486] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#144066] flex items-center gap-2">
            <Plus size={16} /> Add Employee
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, code, phone..."
            className="border border-slate-200 rounded-md pl-9 pr-3 py-2 text-sm outline-none w-64" data-testid="search-employees" />
        </div>
        <select value={filterType} onChange={e => setFilterType(e.target.value)} className="border border-slate-200 rounded-md px-3 py-2 text-sm outline-none">
          <option value="">All Types</option>
          {EMP_TYPES.map(t => <option key={t} value={t}>{formatStatus(t)}</option>)}
        </select>
      </div>

      {loading ? <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 border-2 border-[#1B5486] border-t-transparent rounded-full" /></div> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {employees.map(emp => (
            <div key={emp.id} onClick={() => viewEmployee(emp.id)} data-testid={`emp-card-${emp.id}`}
              className="bg-white rounded-lg border border-slate-200 p-4 hover:border-[#1B5486]/30 hover:shadow-sm cursor-pointer transition-all">
              <div className="flex items-center gap-3">
                <EmployeeAvatar name={emp.name} photoUrl={emp.photo_url} size="sm" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-semibold text-slate-900 truncate">{emp.name}</h3>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${emp.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {emp.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">{emp.employee_code} | {formatStatus(emp.type)}</p>
                  {emp.phone && <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1"><Phone size={10} /> {emp.phone}</p>}
                  {emp.designation && <p className="text-xs text-slate-400">{emp.designation}</p>}
                </div>
                <ChevronRight size={16} className="text-slate-300 flex-shrink-0 hidden sm:block" />
              </div>
            </div>
          ))}
          {employees.length === 0 && <div className="col-span-full text-center py-12 text-sm text-slate-400">No employees found</div>}
          <div className="col-span-full"><Pagination currentPage={page} totalCount={totalCount} pageSize={PAGE_SIZE} onPageChange={setPage} /></div>
        </div>
      )}

      {showCreate && <CreateEmployeeModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchEmployees(); }} />}
    </div>
  );
}

function EmployeeAvatar({ name, photoUrl, size = 'sm' }) {
  const sizeClass = size === 'lg' ? 'w-12 h-12' : 'w-10 h-10';
  const textSize = size === 'lg' ? 'text-base' : 'text-sm';
  if (photoUrl) {
    return <img src={photoUrl} alt={name} className={`${sizeClass} rounded-full object-cover flex-shrink-0`} data-testid="employee-avatar" />;
  }
  const initials = (name || '').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div className={`${sizeClass} rounded-full bg-[#1B5486]/10 flex items-center justify-center flex-shrink-0`} data-testid="employee-avatar-placeholder">
      <span className={`${textSize} font-semibold text-[#1B5486]`}>{initials || <User size={18} />}</span>
    </div>
  );
}

function EmployeeDetail({ emp, onBack, onRefresh }) {
  const { user } = useAuth();
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [activeTab, setActiveTab] = useState('info');

  const startEdit = () => { setEditForm({...emp}); setEditMode(true); };
  const saveEdit = async () => {
    try {
      await api.patch(`/employees/${emp.id}`, editForm);
      setEditMode(false);
      onRefresh();
    } catch (err) { alert('Save failed'); }
  };
  const deleteEmployee = async () => {
    if (!window.confirm(`Delete ${emp.name}? This will move them to Deleted Items.`)) return;
    try {
      await api.delete(`/employees/${emp.id}`);
      onBack();
    } catch (err) { alert(err.response?.data?.detail || 'Delete failed'); }
  };
  const canDelete = ['DIRECTOR', 'HR_ADMIN'].includes(user?.role);

  return (
    <div data-testid="employee-detail" className="space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="text-slate-400 hover:text-slate-600"><ArrowLeft size={20} /></button>
        <div className="flex items-center gap-3 flex-1">
          <EmployeeAvatar name={emp.name} photoUrl={emp.photo_url} size="lg" />
          <div>
            <h1 className="text-xl font-semibold text-slate-900 font-heading">{emp.name}</h1>
            <p className="text-sm text-slate-500">{emp.employee_code} | {formatStatus(emp.type)} | {emp.designation}</p>
          </div>
        </div>
        <span className={`text-xs px-2 py-1 rounded-full ${emp.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {emp.is_active ? 'Active' : 'Inactive'}
        </span>
        {['DIRECTOR', 'HR_ADMIN'].includes(user?.role) && !editMode && (
          <div className="flex items-center gap-2">
            <button onClick={startEdit} className="text-sm text-[#1B5486] hover:underline flex items-center gap-1"><Edit2 size={14} /> Edit</button>
            <button onClick={deleteEmployee} data-testid="delete-employee-btn" className="text-sm text-red-500 hover:underline flex items-center gap-1"><Trash2 size={14} /> Delete</button>
          </div>
        )}
        {editMode && (
          <div className="flex gap-2">
            <button onClick={() => setEditMode(false)} className="text-sm border border-slate-200 px-3 py-1 rounded">Cancel</button>
            <button onClick={saveEdit} className="text-sm bg-[#1B5486] text-white px-3 py-1 rounded">Save</button>
          </div>
        )}
      </div>

      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        {['info', 'attendance', 'salary', 'advances', 'petrol', 'travel', 'ppe', 'dress'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-xs font-medium uppercase tracking-wider border-b-2 transition-colors whitespace-nowrap ${activeTab === tab ? 'border-[#1B5486] text-[#1B5486]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            {tab === 'info' ? 'Details' : tab === 'attendance' ? 'Attendance' : tab === 'salary' ? 'Salary' : tab === 'advances' ? 'Advances' : tab === 'petrol' ? 'Fuel' : tab === 'travel' ? 'Travel' : tab === 'ppe' ? 'PPE' : 'Dress'}
          </button>
        ))}
      </div>

      {activeTab === 'info' && (
        editMode ? <EditInfoPanel form={editForm} setForm={setEditForm} emp={emp} /> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Personal Info</h3>
              <div className="space-y-2 text-sm">
                <Info label="Father's Name" value={emp.father_name} />
                <Info label="Phone" value={emp.phone} />
                <Info label="Email" value={emp.email} />
                <Info label="Address" value={emp.address} />
                <Info label="Joining Date" value={formatDate(emp.joining_date)} />
                <Info label="Aadhar" value={emp.aadhar_no} />
                <Info label="PAN" value={emp.pan_no} />
                <Info label="Blood Group" value={emp.blood_group} />
                <Info label="Emergency Contact" value={emp.emergency_contact_name ? `${emp.emergency_contact_name} - ${emp.emergency_contact_phone}` : '-'} />
              </div>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Bank & Compliance</h3>
              <div className="space-y-2 text-sm">
                <Info label="Bank" value={emp.bank_name} />
                <Info label="Account No" value={emp.bank_account_no} />
                <Info label="IFSC" value={emp.ifsc_code} />
                <Info label="ESI Number" value={emp.esi_number} />
                <Info label="PF Number" value={emp.pf_number} />
                <Info label="UAN" value={emp.uan_number} />
                <Info label="Monthly Salary" value={emp.monthly_salary ? formatINRFull(emp.monthly_salary) : '-'} />
                <Info label="Daily Wage" value={emp.daily_wage ? formatINRFull(emp.daily_wage) : '-'} />
              </div>
            </div>
          </div>
        )
      )}

      {activeTab === 'attendance' && (
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-900">Attendance Calendar</h3>
            <MarkAttendanceButton employeeId={emp.id} onMarked={onRefresh} />
          </div>
          {emp.recent_attendance?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {emp.recent_attendance.map(a => (
                <div key={a.id} title={`${a.date} - ${a.status}`}
                  className={`w-8 h-8 rounded text-[10px] flex items-center justify-center ${a.status === 'PRESENT' ? 'bg-green-100 text-green-700' : a.status === 'ABSENT' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                  {new Date(a.date).getDate()}
                </div>
              ))}
            </div>
          )}
          {(!emp.recent_attendance || emp.recent_attendance.length === 0) && (
            <p className="text-sm text-slate-400">No attendance records yet. Use the button above to mark attendance.</p>
          )}
        </div>
      )}

      {activeTab === 'advances' && emp.advances?.length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Advance Payments</h3>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-100">
              <th className="text-left py-2 text-xs text-slate-500">Date</th>
              <th className="text-right py-2 text-xs text-slate-500">Amount</th>
              <th className="text-right py-2 text-xs text-slate-500">Recovered</th>
              <th className="text-right py-2 text-xs text-slate-500">Balance</th>
              <th className="text-right py-2 text-xs text-slate-500">Monthly Ded.</th>
              <th className="text-left py-2 text-xs text-slate-500">Status</th>
            </tr></thead>
            <tbody>{emp.advances.map(a => (
              <tr key={a.id} className="border-b border-slate-50" data-testid={`advance-row-${a.id}`}>
                <td className="py-2">{formatDate(a.date)}</td>
                <td className="py-2 text-right font-mono">{formatINRFull(a.amount)}</td>
                <td className="py-2 text-right font-mono text-emerald-600">{formatINRFull(a.total_recovered || 0)}</td>
                <td className="py-2 text-right font-mono">{formatINRFull(a.balance)}</td>
                <td className="py-2 text-right font-mono">{formatINRFull(a.monthly_deduction || 0)}</td>
                <td className="py-2"><span className={`text-xs px-1.5 py-0.5 rounded ${a.status === 'ACTIVE' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{a.status}</span></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {activeTab === 'salary' && <SalaryTab employee={emp} />}
      {activeTab === 'petrol' && <PetrolTab employeeId={emp.id} />}
      {activeTab === 'travel' && <TravelTab employeeId={emp.id} employee={emp} />}
      {activeTab === 'ppe' && <PPETab employeeId={emp.id} />}
      {activeTab === 'dress' && <DressTab employeeId={emp.id} />}
    </div>
  );
}


function MarkAttendanceButton({ employeeId, onMarked }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [status, setStatus] = useState('PRESENT');
  const [saving, setSaving] = useState(false);

  const handleMark = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/employees/attendance', { employee_id: employeeId, date, status });
      setOpen(false);
      onMarked();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  if (!open) return <button onClick={() => setOpen(true)} data-testid="mark-attendance-btn" className="bg-[#1B5486] text-white text-xs px-3 py-1.5 rounded-lg flex items-center gap-1"><Plus size={12} /> Mark Attendance</button>;

  return (
    <form onSubmit={handleMark} className="flex items-end gap-2 bg-slate-50 border border-slate-200 rounded-lg p-2" data-testid="mark-attendance-form">
      <div>
        <label className="block text-[10px] font-medium text-slate-500 mb-0.5">Date</label>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} required className="input-field text-xs" data-testid="attendance-date-input" />
      </div>
      <div>
        <label className="block text-[10px] font-medium text-slate-500 mb-0.5">Status</label>
        <select value={status} onChange={e => setStatus(e.target.value)} className="input-field text-xs" data-testid="attendance-status-select">
          <option value="PRESENT">Present</option>
          <option value="HALF_DAY">Half Day</option>
          <option value="ABSENT">Absent</option>
        </select>
      </div>
      <button type="submit" disabled={saving} className="bg-emerald-600 text-white text-xs px-3 py-1.5 rounded-lg" data-testid="attendance-save-btn">{saving ? 'Saving...' : 'Save'}</button>
      <button type="button" onClick={() => setOpen(false)} className="text-xs text-slate-500 px-2 py-1.5">Cancel</button>
    </form>
  );
}


function EditInfoPanel({ form, setForm, emp }) {
  const token = localStorage.getItem('access_token');
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-3">
        <h3 className="text-sm font-semibold text-slate-900">Personal Info</h3>
        <div className="space-y-2">
          {[['name','Name'],['father_name',"Father's Name"],['phone','Phone'],['email','Email'],['designation','Designation']].map(([k,l]) => (
            <div key={k}><label className="block text-xs text-slate-500 mb-0.5">{l}</label>
              <input value={form[k] || ''} onChange={e => setForm({...form, [k]: e.target.value})} className="input-field w-full" /></div>
          ))}
          <div><label className="block text-xs text-slate-500 mb-0.5">Type</label>
            <select value={form.type || ''} onChange={e => setForm({...form, type: e.target.value})} className="input-field w-full">
              {EMP_TYPES.map(t => <option key={t} value={t}>{formatStatus(t)}</option>)}</select></div>
          <div><label className="block text-xs text-slate-500 mb-0.5">Joining Date</label>
            <input type="date" value={form.joining_date || ''} onChange={e => setForm({...form, joining_date: e.target.value})} className="input-field w-full" /></div>
          <div><label className="block text-xs text-slate-500 mb-0.5">Address</label>
            <textarea value={form.address || ''} onChange={e => setForm({...form, address: e.target.value})} rows={2} className="input-field w-full" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="block text-xs text-slate-500 mb-0.5">Aadhar No</label>
              <input value={form.aadhar_no || ''} onChange={e => setForm({...form, aadhar_no: e.target.value})} className="input-field w-full" /></div>
            <div><label className="block text-xs text-slate-500 mb-0.5">PAN No</label>
              <input value={form.pan_no || ''} onChange={e => setForm({...form, pan_no: e.target.value})} className="input-field w-full" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="block text-xs text-slate-500 mb-0.5">Blood Group</label>
              <select value={form.blood_group || ''} onChange={e => setForm({...form, blood_group: e.target.value})} className="input-field w-full">
                <option value="">Select</option>
                {['A+','A-','B+','B-','O+','O-','AB+','AB-'].map(bg => <option key={bg}>{bg}</option>)}</select></div>
            <div><label className="block text-xs text-slate-500 mb-0.5">Active</label>
              <select value={form.is_active === false ? 'false' : 'true'} onChange={e => setForm({...form, is_active: e.target.value === 'true'})} className="input-field w-full">
                <option value="true">Active</option>
                <option value="false">Inactive</option></select></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="block text-xs text-slate-500 mb-0.5">Emergency Contact</label>
              <input value={form.emergency_contact_name || ''} onChange={e => setForm({...form, emergency_contact_name: e.target.value})} placeholder="Name" className="input-field w-full" /></div>
            <div><label className="block text-xs text-slate-500 mb-0.5">Emergency Phone</label>
              <input type="tel" value={form.emergency_contact_phone || ''} onChange={e => setForm({...form, emergency_contact_phone: e.target.value.replace(/[^0-9]/g, '')})} className="input-field w-full" /></div>
          </div>
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Employee Photo</label>
          <FileUpload token={token} accept=".jpg,.jpeg,.png" label="Upload employee photo"
            onUploaded={(url) => setForm({...form, photo_url: url})} />
          {form.photo_url && <p className="text-xs text-emerald-600 mt-1">Photo uploaded</p>}
        </div>
      </div>
      <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-3">
        <h3 className="text-sm font-semibold text-slate-900">Bank & Compliance</h3>
        <div className="space-y-2">
          {[['bank_name','Bank Name'],['bank_account_no','Account No'],['ifsc_code','IFSC Code']].map(([k,l]) => (
            <div key={k}><label className="block text-xs text-slate-500 mb-0.5">{l}</label>
              <input value={form[k] || ''} onChange={e => setForm({...form, [k]: e.target.value})} className="input-field w-full" /></div>
          ))}
          {[['esi_number','ESI Number'],['pf_number','PF Number'],['uan_number','UAN']].map(([k,l]) => (
            <div key={k}><label className="block text-xs text-slate-500 mb-0.5">{l}</label>
              <input value={form[k] || ''} onChange={e => setForm({...form, [k]: e.target.value})} className="input-field w-full" /></div>
          ))}
          <div className="grid grid-cols-2 gap-2">
            <div><label className="block text-xs text-slate-500 mb-0.5">Monthly Salary</label>
              <input type="number" value={form.monthly_salary || ''} onChange={e => setForm({...form, monthly_salary: e.target.value})} className="input-field w-full" /></div>
            <div><label className="block text-xs text-slate-500 mb-0.5">Daily Wage</label>
              <input type="number" value={form.daily_wage || ''} onChange={e => setForm({...form, daily_wage: e.target.value})} className="input-field w-full" /></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SalaryTab({ employee }) {
  const { user } = useAuth();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [computed, setComputed] = useState(null);
  const [salaryRecords, setSalaryRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({});

  const canCreate = ['DIRECTOR', 'HR_ADMIN', 'ACCOUNTANT'].includes(user?.role);

  useEffect(() => {
    api.get(`/employees/salary/list?employee_id=${employee.id}`).then(r => setSalaryRecords(r.data.salary_records || []));
  }, [employee.id]);

  const fetchComputed = async () => {
    if (!month) return;
    setLoading(true);
    try {
      const res = await api.get(`/employees/${employee.id}/salary-compute?month=${month}`);
      const c = res.data;
      setComputed(c);
      setForm({
        employee_id: employee.id, month, basic_salary: c.basic_salary,
        days_worked: c.days_worked, total_days_in_month: c.total_days_in_month,
        tds: 0, advance_deduction: c.advance_deduction, other_deductions: 0,
        payment_status: 'PENDING', payment_date: '', payment_mode: 'BANK_TRANSFER'
      });
      setShowForm(true);
    } catch { alert('Failed to compute'); }
    setLoading(false);
  };

  const saveSalary = async (e) => {
    e.preventDefault();
    try {
      await api.post('/employees/salary', form);
      setShowForm(false);
      setComputed(null);
      const res = await api.get(`/employees/salary/list?employee_id=${employee.id}`);
      setSalaryRecords(res.data.salary_records || []);
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4">
      {canCreate && (
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Generate Salary Slip</h3>
          <div className="flex items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Month</label>
              <input type="month" value={month} onChange={e => { setMonth(e.target.value); setShowForm(false); setComputed(null); }}
                className="border rounded-lg px-3 py-2 text-sm" data-testid="salary-month-input" />
            </div>
            <button onClick={fetchComputed} disabled={loading} data-testid="compute-salary-btn"
              className="bg-[#1B5486] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#144066] disabled:opacity-50">
              {loading ? 'Computing...' : 'Compute from Attendance'}
            </button>
          </div>

          {computed && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="attendance-summary">
              <p className="text-xs font-semibold text-blue-800 mb-1">Attendance Summary for {month}</p>
              <div className="flex gap-4 text-xs text-blue-700">
                <span>Present: <strong>{computed.attendance.present}</strong></span>
                <span>Half Day: <strong>{computed.attendance.half_day}</strong></span>
                <span>Absent: <strong>{computed.attendance.absent}</strong></span>
                <span>Days Worked: <strong>{computed.days_worked}</strong></span>
              </div>
            </div>
          )}

          {/* EMP-02: Advance breakdown */}
          {computed?.advance_breakdown?.length > 0 && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="advance-breakdown">
              <p className="text-xs font-semibold text-amber-800 mb-2">Active Advance Deductions</p>
              {computed.advance_breakdown.map((ab, i) => (
                <div key={i} className="flex justify-between text-xs text-amber-700 py-0.5">
                  <span>{ab.reason || 'Advance'} (Balance: {formatINRFull(ab.balance)})</span>
                  <span className="font-semibold">-{formatINRFull(ab.this_month_deduction)}</span>
                </div>
              ))}
              <div className="border-t border-amber-300 mt-1 pt-1 flex justify-between text-xs font-semibold text-amber-800">
                <span>Total Advance Deduction</span>
                <span>-{formatINRFull(computed.advance_deduction)}</span>
              </div>
            </div>
          )}

          {showForm && (
            <form onSubmit={saveSalary} className="mt-4 space-y-3" data-testid="salary-form">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">In Hand Salary</label>
                  <input type="number" value={form.basic_salary} onChange={e => setForm({...form, basic_salary: parseFloat(e.target.value) || 0})}
                    className="input-field w-full" data-testid="salary-basic-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Days Worked</label>
                  <input type="number" step="0.5" value={form.days_worked} onChange={e => setForm({...form, days_worked: parseFloat(e.target.value) || 0})}
                    className="input-field w-full" data-testid="salary-days-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Total Days</label>
                  <input type="number" value={form.total_days_in_month} onChange={e => setForm({...form, total_days_in_month: parseInt(e.target.value) || 26})}
                    className="input-field w-full" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Prorated In Hand</label>
                  <div className="input-field w-full bg-slate-100 text-slate-700 text-xs" data-testid="salary-prorated-display">
                    {formatINRFull(computed?.prorated_in_hand || 0)}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">PF (Employee) <span className="text-[10px] text-slate-400">12%</span></label>
                  <div className="input-field w-full bg-slate-100 text-slate-700 text-xs" data-testid="salary-pf-emp-display">{formatINRFull(computed?.pf_employee || 0)}</div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">PF (Employer) <span className="text-[10px] text-slate-400">12%</span></label>
                  <div className="input-field w-full bg-slate-100 text-slate-700 text-xs" data-testid="salary-pf-employer-display">{formatINRFull(computed?.pf_employer || 0)}</div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">ESI (Employee) <span className="text-[10px] text-slate-400">0.75%</span></label>
                  <div className="input-field w-full bg-slate-100 text-slate-700 text-xs" data-testid="salary-esi-emp-display">{formatINRFull(computed?.esi_employee || 0)}</div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">ESI (Employer) <span className="text-[10px] text-slate-400">3.25%</span></label>
                  <div className="input-field w-full bg-slate-100 text-slate-700 text-xs" data-testid="salary-esi-employer-display">{formatINRFull(computed?.esi_employer || 0)}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">TDS</label>
                  <input type="number" value={form.tds} onChange={e => setForm({...form, tds: parseFloat(e.target.value) || 0})} className="input-field w-full" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Advance Ded.</label>
                  <div className="input-field w-full bg-amber-50 text-amber-700 text-xs font-semibold" data-testid="salary-advance-display">
                    {formatINRFull(form.advance_deduction || 0)}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Other Ded.</label>
                  <input type="number" value={form.other_deductions} onChange={e => setForm({...form, other_deductions: parseFloat(e.target.value) || 0})} className="input-field w-full" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Gross Salary</label>
                  <div className="input-field w-full bg-blue-50 text-blue-700 font-semibold" data-testid="salary-gross-display">
                    {formatINRFull(computed?.gross_salary || 0)}
                  </div>
                  <p className="text-[9px] text-slate-400 mt-0.5">In Hand + PF(E+Er) + ESI(E+Er) + TDS + Other</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Net Salary</label>
                  <div className="input-field w-full bg-emerald-50 text-emerald-700 font-semibold" data-testid="salary-net-display">{formatINRFull(computed?.net_salary || 0)}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Payment Status</label>
                  <select value={form.payment_status} onChange={e => setForm({...form, payment_status: e.target.value})} className="input-field w-full" data-testid="salary-status-select">
                    <option value="PENDING">Pending</option><option value="PAID">Paid</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Payment Mode</label>
                  <select value={form.payment_mode} onChange={e => setForm({...form, payment_mode: e.target.value})} className="input-field w-full">
                    <option value="BANK_TRANSFER">Bank Transfer</option><option value="CASH">Cash</option><option value="UPI">UPI</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <button type="submit" data-testid="save-salary-btn" className="bg-[#1B5486] text-white px-4 py-2 rounded-lg text-sm">Save Salary Record</button>
                <button type="button" onClick={() => { setShowForm(false); setComputed(null); }} className="border px-4 py-2 rounded-lg text-sm">Cancel</button>
              </div>
            </form>
          )}
        </div>
      )}

      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Salary Records</h3>
        {salaryRecords.length === 0 ? <p className="text-sm text-slate-400">No salary records</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-100">
                <th className="text-left py-2 text-xs text-slate-500">Month</th>
                <th className="text-right py-2 text-xs text-slate-500">Days</th>
                <th className="text-right py-2 text-xs text-slate-500">Gross</th>
                <th className="text-right py-2 text-xs text-slate-500">PF (E+Er)</th>
                <th className="text-right py-2 text-xs text-slate-500">ESI (E+Er)</th>
                <th className="text-right py-2 text-xs text-slate-500">Adv. Ded.</th>
                <th className="text-right py-2 text-xs text-slate-500">Net</th>
                <th className="text-left py-2 text-xs text-slate-500">Status</th>
              </tr></thead>
              <tbody>{salaryRecords.map(r => (
                <tr key={r.id} className="border-b border-slate-50" data-testid={`salary-row-${r.id}`}>
                  <td className="py-2 font-medium">{r.month}</td>
                  <td className="py-2 text-right">{r.days_worked}</td>
                  <td className="py-2 text-right font-mono">{formatINRFull(r.gross_salary)}</td>
                  <td className="py-2 text-right font-mono">{formatINRFull((r.pf_employee || 0) + (r.pf_employer || 0))}</td>
                  <td className="py-2 text-right font-mono">{formatINRFull((r.esi_employee || 0) + (r.esi_employer || 0))}</td>
                  <td className="py-2 text-right font-mono text-amber-600">{formatINRFull(r.advance_deduction || 0)}</td>
                  <td className="py-2 text-right font-mono font-medium">{formatINRFull(r.net_salary)}</td>
                  <td className="py-2"><span className={`text-xs px-1.5 py-0.5 rounded ${r.payment_status === 'PAID' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{r.payment_status}</span></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// EMP-04: Petrol tab with status and approve/reject
function PetrolTab({ employeeId }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ date: new Date().toISOString().slice(0, 10), vehicle_number: '', liters: '', amount: '', project_id: '', km_reading: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const canApprove = ['DIRECTOR', 'HR_ADMIN', 'ACCOUNTANT'].includes(user?.role);
  const canAdd = ['DIRECTOR', 'HR_ADMIN', 'ACCOUNTANT', 'SUPERVISOR'].includes(user?.role);

  const fetchItems = () => {
    api.get(`/employees/petrol/list?employee_id=${employeeId}`).then(r => setItems(r.data.petrol_expenses || []));
  };
  useEffect(() => { fetchItems(); }, [employeeId]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/employees/petrol/${id}`, { status });
      fetchItems();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/employees/petrol', { ...addForm, employee_id: employeeId, liters: parseFloat(addForm.liters) || 0, amount: parseFloat(addForm.amount) || 0 });
      setShowAdd(false);
      setAddForm({ date: new Date().toISOString().slice(0, 10), vehicle_number: '', liters: '', amount: '', project_id: '', km_reading: '', notes: '' });
      fetchItems();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-900">Fuel Expenses</h3>
        {canAdd && <button onClick={() => setShowAdd(!showAdd)} data-testid="add-petrol-btn" className="text-xs bg-[#1B5486] text-white px-3 py-1.5 rounded-lg flex items-center gap-1"><Plus size={12} /> Add Fuel Entry</button>}
      </div>
      {showAdd && (
        <form onSubmit={handleAdd} className="mb-4 p-3 bg-slate-50 rounded-lg" data-testid="add-petrol-form">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
            <div><label className="block text-[10px] font-medium text-slate-500 mb-0.5">Date *</label>
              <input type="date" value={addForm.date} onChange={e => setAddForm({...addForm, date: e.target.value})} required className="input-field w-full text-xs" /></div>
            <div><label className="block text-[10px] font-medium text-slate-500 mb-0.5">Vehicle No.</label>
              <input value={addForm.vehicle_number} onChange={e => setAddForm({...addForm, vehicle_number: e.target.value})} placeholder="e.g. TN-07-AB-1234" className="input-field w-full text-xs" /></div>
            <div><label className="block text-[10px] font-medium text-slate-500 mb-0.5">Litres *</label>
              <input type="number" step="0.01" value={addForm.liters} onChange={e => setAddForm({...addForm, liters: e.target.value})} required className="input-field w-full text-xs" /></div>
            <div><label className="block text-[10px] font-medium text-slate-500 mb-0.5">Amount *</label>
              <input type="number" step="0.01" value={addForm.amount} onChange={e => setAddForm({...addForm, amount: e.target.value})} required className="input-field w-full text-xs" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div><label className="block text-[10px] font-medium text-slate-500 mb-0.5">KM Reading</label>
              <input value={addForm.km_reading} onChange={e => setAddForm({...addForm, km_reading: e.target.value})} className="input-field w-full text-xs" /></div>
            <div><label className="block text-[10px] font-medium text-slate-500 mb-0.5">Notes</label>
              <input value={addForm.notes} onChange={e => setAddForm({...addForm, notes: e.target.value})} className="input-field w-full text-xs" /></div>
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={saving} data-testid="submit-petrol-btn" className="bg-emerald-600 text-white text-xs px-3 py-1.5 rounded-lg">{saving ? 'Saving...' : 'Save'}</button>
            <button type="button" onClick={() => setShowAdd(false)} className="text-xs text-slate-500 px-2 py-1.5">Cancel</button>
          </div>
        </form>
      )}
      {items.length === 0 ? <p className="text-sm text-slate-400">No fuel records</p> : (
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-100">
            <th className="text-left py-2 text-xs text-slate-500">Date</th>
            <th className="text-left py-2 text-xs text-slate-500">Vehicle</th>
            <th className="text-left py-2 text-xs text-slate-500">Project</th>
            <th className="text-right py-2 text-xs text-slate-500">Litres</th>
            <th className="text-right py-2 text-xs text-slate-500">Amount</th>
            <th className="text-left py-2 text-xs text-slate-500">Status</th>
            {canApprove && <th className="text-right py-2 text-xs text-slate-500">Actions</th>}
          </tr></thead>
          <tbody>{items.map(i => (
            <tr key={i.id} className="border-b border-slate-50" data-testid={`petrol-row-${i.id}`}>
              <td className="py-2">{formatDate(i.date)}</td>
              <td className="py-2">{i.vehicle_number || '-'}</td>
              <td className="py-2 text-xs">{i.project_id ? 'Tagged' : '-'}</td>
              <td className="py-2 text-right">{i.liters}</td>
              <td className="py-2 text-right font-mono">{formatINRFull(i.amount)}</td>
              <td className="py-2">
                <span className={`text-xs px-1.5 py-0.5 rounded ${
                  i.status === 'APPROVED' ? 'bg-green-100 text-green-700' :
                  i.status === 'REJECTED' ? 'bg-red-100 text-red-700' :
                  'bg-amber-100 text-amber-700'
                }`}>{i.status || 'PENDING'}</span>
              </td>
              {canApprove && (
                <td className="py-2 text-right">
                  {(!i.status || i.status === 'PENDING') && (
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => updateStatus(i.id, 'APPROVED')} data-testid={`approve-petrol-${i.id}`}
                        className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200 flex items-center gap-0.5">
                        <Check size={12} /> Approve
                      </button>
                      <button onClick={() => updateStatus(i.id, 'REJECTED')} data-testid={`reject-petrol-${i.id}`}
                        className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 flex items-center gap-0.5">
                        <X size={12} /> Reject
                      </button>
                    </div>
                  )}
                </td>
              )}
            </tr>
          ))}</tbody>
        </table>
      )}
    </div>
  );
}

// EMP-03: Travel tab with policy-based auto-calculation
function TravelTab({ employeeId, employee }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ from_location: '', to_location: '', date: new Date().toISOString().slice(0, 10), mode_of_travel: 'OWN_VEHICLE', distance_km: 0, rate_per_km: 0, amount: 0, num_days: 0, purpose: '', project_id: '' });
  const [policyMatch, setPolicyMatch] = useState(null);

  const fetchItems = () => {
    api.get(`/employees/travel-allowance/list?employee_id=${employeeId}`).then(r => setItems(r.data.travel_allowances || []));
  };
  useEffect(() => { fetchItems(); }, [employeeId]);
  useEffect(() => {
    api.get('/employees/ta-policies?active_only=true').then(r => setPolicies(r.data.policies || []));
  }, []);

  // Auto-pick policy when num_days changes
  useEffect(() => {
    if (form.num_days > 0 && employee?.type && policies.length > 0) {
      const matched = policies.find(p => p.employee_type === employee.type);
      setPolicyMatch(matched || null);
    } else {
      setPolicyMatch(null);
    }
  }, [form.num_days, policies, employee?.type]);

  const policyCalc = policyMatch && form.num_days > 0 ? round2(form.num_days * (policyMatch.daily_total || 0)) : 0;

  const submitClaim = async (e) => {
    e.preventDefault();
    try {
      await api.post('/employees/travel-allowance', {
        employee_id: employeeId,
        ...form,
        da_amount: policyCalc || parseFloat(form.da_amount || 0),
        policy_id: policyMatch?.id || null
      });
      setShowCreate(false);
      setForm({ from_location: '', to_location: '', date: new Date().toISOString().slice(0, 10), mode_of_travel: 'OWN_VEHICLE', distance_km: 0, rate_per_km: 0, amount: 0, num_days: 0, purpose: '', project_id: '' });
      setPolicyMatch(null);
      fetchItems();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-900">Travel Allowances</h3>
          <button onClick={() => setShowCreate(!showCreate)} data-testid="new-travel-claim-btn"
            className="text-xs bg-[#1B5486] text-white px-3 py-1.5 rounded-md hover:bg-[#144066]">
            {showCreate ? 'Cancel' : '+ New Claim'}
          </button>
        </div>

        {showCreate && (
          <form onSubmit={submitClaim} className="mb-4 p-3 border border-slate-200 rounded-lg bg-slate-50 space-y-3" data-testid="travel-claim-form">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Date</label>
                <input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="input-field w-full" /></div>
              <div><label className="block text-xs font-medium text-slate-600 mb-1">From</label>
                <input value={form.from_location} onChange={e => setForm({...form, from_location: e.target.value})} className="input-field w-full" /></div>
              <div><label className="block text-xs font-medium text-slate-600 mb-1">To</label>
                <input value={form.to_location} onChange={e => setForm({...form, to_location: e.target.value})} className="input-field w-full" /></div>
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Mode</label>
                <select value={form.mode_of_travel} onChange={e => setForm({...form, mode_of_travel: e.target.value})} className="input-field w-full">
                  <option value="OWN_VEHICLE">Own Vehicle</option><option value="BUS">Bus</option><option value="TRAIN">Train</option><option value="FLIGHT">Flight</option><option value="TAXI">Taxi</option>
                </select></div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Distance (km)</label>
                <input type="number" value={form.distance_km} onChange={e => setForm({...form, distance_km: parseFloat(e.target.value) || 0})} className="input-field w-full" /></div>
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Travel Amount</label>
                <input type="number" value={form.amount} onChange={e => setForm({...form, amount: parseFloat(e.target.value) || 0})} className="input-field w-full" /></div>
              <div><label className="block text-xs font-medium text-slate-600 mb-1">No. of Days</label>
                <input type="number" step="0.5" value={form.num_days} onChange={e => setForm({...form, num_days: parseFloat(e.target.value) || 0})} className="input-field w-full" data-testid="travel-num-days" /></div>
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Purpose</label>
                <input value={form.purpose} onChange={e => setForm({...form, purpose: e.target.value})} className="input-field w-full" /></div>
            </div>

            {policyMatch && form.num_days > 0 && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="policy-auto-calc">
                <p className="text-xs font-semibold text-blue-800 mb-1">Policy: {policyMatch.name}</p>
                <div className="flex gap-4 text-xs text-blue-700">
                  <span>Food: {formatINRFull(policyMatch.daily_food)}/day</span>
                  <span>Accom: {formatINRFull(policyMatch.daily_accommodation)}/day</span>
                  <span>Transport: {formatINRFull(policyMatch.daily_local_transport)}/day</span>
                </div>
                <p className="text-xs text-blue-800 font-semibold mt-1">
                  {form.num_days} days x {formatINRFull(policyMatch.daily_total)}/day = <span className="text-sm" data-testid="policy-calc-amount">{formatINRFull(policyCalc)}</span>
                </p>
              </div>
            )}

            <div className="flex gap-2">
              <button type="submit" data-testid="submit-travel-claim-btn" className="bg-[#1B5486] text-white px-4 py-2 rounded-lg text-sm">Submit Claim</button>
              <button type="button" onClick={() => setShowCreate(false)} className="border px-4 py-2 rounded-lg text-sm">Cancel</button>
            </div>
          </form>
        )}

        {items.length === 0 ? <p className="text-sm text-slate-400">No travel records</p> : (
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-100">
              <th className="text-left py-2 text-xs text-slate-500">Date</th>
              <th className="text-left py-2 text-xs text-slate-500">From - To</th>
              <th className="text-right py-2 text-xs text-slate-500">Days</th>
              <th className="text-right py-2 text-xs text-slate-500">DA/Policy</th>
              <th className="text-right py-2 text-xs text-slate-500">Total</th>
              <th className="text-left py-2 text-xs text-slate-500">Status</th>
            </tr></thead>
            <tbody>{items.map(i => (
              <tr key={i.id} className="border-b border-slate-50" data-testid={`travel-row-${i.id}`}>
                <td className="py-2">{formatDate(i.date)}</td>
                <td className="py-2 text-xs">{i.from_location} - {i.to_location}</td>
                <td className="py-2 text-right">{i.num_days || '-'}</td>
                <td className="py-2 text-right font-mono">{formatINRFull(i.da_amount || i.policy_calculated_amount || 0)}</td>
                <td className="py-2 text-right font-mono">{formatINRFull(i.total_amount)}</td>
                <td className="py-2"><span className={`text-xs px-1.5 py-0.5 rounded ${i.status === 'APPROVED' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{i.status}</span></td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function round2(n) { return Math.round(n * 100) / 100; }

function Info({ label, value }) {
  const isLong = typeof value === 'string' && value.length > 40;
  return isLong ? (
    <div>
      <span className="text-slate-500">{label}</span>
      <p className="text-slate-900 font-medium mt-0.5 break-words">{value}</p>
    </div>
  ) : (
    <div className="flex justify-between gap-4">
      <span className="text-slate-500 flex-shrink-0">{label}</span>
      <span className="text-slate-900 font-medium text-right break-all">{value || '-'}</span>
    </div>
  );
}

function CreateEmployeeModal({ onClose, onCreated }) {
  const token = localStorage.getItem('access_token');
  const [form, setForm] = useState({
    name: '', father_name: '', phone: '', email: '', type: 'TECHNICIAN', designation: '',
    joining_date: new Date().toISOString().slice(0, 10), monthly_salary: '', daily_wage: '',
    bank_account_no: '', bank_name: '', ifsc_code: '', aadhar_no: '', pan_no: '', address: '',
    emergency_contact_name: '', emergency_contact_phone: '', blood_group: '', reporting_to_id: '',
    photo_url: '', esi_number: '', pf_number: '', uan_number: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.post('/employees', form); onCreated(); } catch (err) { alert(err.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg border w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="create-employee-modal">
        <h2 className="text-lg font-semibold text-slate-900 font-heading mb-4">Add Employee</h2>
        <form onSubmit={handleSubmit} className="space-y-3">
          {/* EMP-05: Photo upload */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Employee Photo</label>
            <FileUpload token={token} accept=".jpg,.jpeg,.png" label="Upload employee photo"
              onUploaded={(url) => setForm({...form, photo_url: url})} />
            {form.photo_url && <p className="text-xs text-emerald-600 mt-1">Photo uploaded</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Name *</label>
              <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="input-field w-full" data-testid="emp-name-input" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Father's Name</label>
              <input value={form.father_name} onChange={e => setForm({...form, father_name: e.target.value})} className="input-field w-full" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Phone</label>
              <input type="tel" inputMode="numeric" pattern="[0-9]*" value={form.phone} onChange={e => { const v = e.target.value.replace(/[^0-9]/g, ''); setForm({...form, phone: v}); }} className="input-field w-full" placeholder="10-digit number" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Email</label>
              <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="input-field w-full" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Type</label>
              <select value={form.type} onChange={e => setForm({...form, type: e.target.value})} className="input-field w-full">
                {EMP_TYPES.map(t => <option key={t} value={t}>{formatStatus(t)}</option>)}</select></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Designation</label>
              <input value={form.designation} onChange={e => setForm({...form, designation: e.target.value})} className="input-field w-full" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Joining Date</label>
              <input type="date" value={form.joining_date} onChange={e => setForm({...form, joining_date: e.target.value})} className="input-field w-full" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Monthly Salary</label>
              <input type="number" value={form.monthly_salary} onChange={e => setForm({...form, monthly_salary: e.target.value})} className="input-field w-full" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Daily Wage</label>
              <input type="number" value={form.daily_wage} onChange={e => setForm({...form, daily_wage: e.target.value})} className="input-field w-full" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Aadhar No</label>
              <input value={form.aadhar_no} onChange={e => setForm({...form, aadhar_no: e.target.value})} className="input-field w-full" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">PAN No</label>
              <input value={form.pan_no} onChange={e => setForm({...form, pan_no: e.target.value})} className="input-field w-full" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">ESI Number</label>
              <input value={form.esi_number} onChange={e => setForm({...form, esi_number: e.target.value})} className="input-field w-full" placeholder="ESI No" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">PF Number</label>
              <input value={form.pf_number} onChange={e => setForm({...form, pf_number: e.target.value})} className="input-field w-full" placeholder="PF No" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">UAN</label>
              <input value={form.uan_number} onChange={e => setForm({...form, uan_number: e.target.value})} className="input-field w-full" placeholder="UAN" /></div>
          </div>
          <div><label className="block text-xs font-medium text-slate-700 mb-1">Address</label>
            <textarea value={form.address} onChange={e => setForm({...form, address: e.target.value})} rows={2} className="input-field w-full" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Emergency Contact</label>
              <input value={form.emergency_contact_name} onChange={e => setForm({...form, emergency_contact_name: e.target.value})} placeholder="Name" className="input-field w-full" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Emergency Phone</label>
              <input type="tel" inputMode="numeric" pattern="[0-9]*" value={form.emergency_contact_phone} onChange={e => { const v = e.target.value.replace(/[^0-9]/g, ''); setForm({...form, emergency_contact_phone: v}); }} className="input-field w-full" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Blood Group</label>
              <select value={form.blood_group} onChange={e => setForm({...form, blood_group: e.target.value})} className="input-field w-full">
                <option value="">Select</option>
                {['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'].map(bg => <option key={bg}>{bg}</option>)}
              </select></div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-slate-200 py-2 rounded-md text-sm">Cancel</button>
            <button type="submit" disabled={saving} data-testid="submit-employee-btn" className="flex-1 bg-[#1B5486] text-white py-2 rounded-md text-sm font-medium hover:bg-[#144066] disabled:opacity-60">
              {saving ? 'Saving...' : 'Add Employee'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}


// D17: PPE Issue Tab
function PPETab({ employeeId }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ item_name: '', item_type: 'HELMET', quantity: 1, issue_date: new Date().toISOString().slice(0, 10), condition: 'NEW', remarks: '' });
  const PPE_TYPES = ['HELMET', 'SAFETY_SHOES', 'SAFETY_GOGGLES', 'GLOVES', 'HI_VIS_VEST', 'EAR_PLUGS', 'HARNESS', 'FACE_SHIELD', 'OTHER'];

  useEffect(() => {
    api.get(`/employees/ppe/list?employee_id=${employeeId}`).then(r => setItems(r.data.items || [])).catch(() => {});
  }, [employeeId]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/employees/ppe', { ...form, employee_id: employeeId });
      const r = await api.get(`/employees/ppe/list?employee_id=${employeeId}`);
      setItems(r.data.items || []);
      setShowForm(false);
      setForm({ item_name: '', item_type: 'HELMET', quantity: 1, issue_date: new Date().toISOString().slice(0, 10), condition: 'NEW', remarks: '' });
    } catch (err) { alert('Failed to add PPE issue'); }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid="ppe-tab">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-900">PPE Issued</h3>
        <button onClick={() => setShowForm(!showForm)} className="text-xs px-3 py-1.5 bg-[#1B5486] text-white rounded-md" data-testid="add-ppe-btn">
          {showForm ? 'Cancel' : '+ Issue PPE'}
        </button>
      </div>
      {showForm && (
        <form onSubmit={submit} className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4 p-3 bg-slate-50 rounded-lg">
          <div><label className="block text-xs font-medium text-slate-600 mb-1">Item Type</label>
            <select value={form.item_type} onChange={e => setForm({...form, item_type: e.target.value})} className="input-field w-full">{PPE_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}</select></div>
          <div><label className="block text-xs font-medium text-slate-600 mb-1">Item Name</label>
            <input value={form.item_name} onChange={e => setForm({...form, item_name: e.target.value})} className="input-field w-full" required /></div>
          <div><label className="block text-xs font-medium text-slate-600 mb-1">Quantity</label>
            <input type="number" value={form.quantity} onChange={e => setForm({...form, quantity: +e.target.value})} className="input-field w-full" min="1" /></div>
          <div><label className="block text-xs font-medium text-slate-600 mb-1">Issue Date</label>
            <input type="date" value={form.issue_date} onChange={e => setForm({...form, issue_date: e.target.value})} className="input-field w-full" /></div>
          <div><label className="block text-xs font-medium text-slate-600 mb-1">Condition</label>
            <select value={form.condition} onChange={e => setForm({...form, condition: e.target.value})} className="input-field w-full">
              <option value="NEW">New</option><option value="USED">Used</option><option value="REPLACEMENT">Replacement</option></select></div>
          <div className="flex items-end"><button type="submit" className="px-4 py-2 bg-emerald-600 text-white rounded-md text-sm font-medium" data-testid="submit-ppe-btn">Issue</button></div>
        </form>
      )}
      {items.length === 0 ? <p className="text-sm text-slate-400 text-center py-4">No PPE issued</p> : (
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-200">
            <th className="text-left py-2 text-xs text-slate-500">Date</th>
            <th className="text-left py-2 text-xs text-slate-500">Type</th>
            <th className="text-left py-2 text-xs text-slate-500">Item</th>
            <th className="text-center py-2 text-xs text-slate-500">Qty</th>
            <th className="text-left py-2 text-xs text-slate-500">Condition</th>
            <th className="text-left py-2 text-xs text-slate-500">Remarks</th>
          </tr></thead>
          <tbody>{items.map(i => (
            <tr key={i.id} className="border-b border-slate-50" data-testid={`ppe-row-${i.id}`}>
              <td className="py-2 text-slate-500">{formatDate(i.issue_date)}</td>
              <td className="py-2"><span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">{(i.item_type || '').replace(/_/g, ' ')}</span></td>
              <td className="py-2 text-slate-900">{i.item_name}</td>
              <td className="py-2 text-center">{i.quantity}</td>
              <td className="py-2"><span className="text-xs px-1.5 py-0.5 rounded bg-slate-100">{i.condition}</span></td>
              <td className="py-2 text-slate-500">{i.remarks || '-'}</td>
            </tr>
          ))}</tbody>
        </table>
      )}
    </div>
  );
}

// D17: Dress/Uniform Issue Tab
function DressTab({ employeeId }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ item_name: '', size: '', quantity: 1, issue_date: new Date().toISOString().slice(0, 10), remarks: '' });

  useEffect(() => {
    api.get(`/employees/dress/list?employee_id=${employeeId}`).then(r => setItems(r.data.dress_issues || [])).catch(() => {});
  }, [employeeId]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/employees/dress', { ...form, employee_id: employeeId });
      const r = await api.get(`/employees/dress/list?employee_id=${employeeId}`);
      setItems(r.data.dress_issues || []);
      setShowForm(false);
      setForm({ item_name: '', size: '', quantity: 1, issue_date: new Date().toISOString().slice(0, 10), remarks: '' });
    } catch (err) { alert('Failed to add dress issue'); }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid="dress-tab">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-900">Dress/Uniform Issued</h3>
        <button onClick={() => setShowForm(!showForm)} className="text-xs px-3 py-1.5 bg-[#1B5486] text-white rounded-md" data-testid="add-dress-btn">
          {showForm ? 'Cancel' : '+ Issue Dress'}
        </button>
      </div>
      {showForm && (
        <form onSubmit={submit} className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4 p-3 bg-slate-50 rounded-lg">
          <div><label className="block text-xs font-medium text-slate-600 mb-1">Item Name</label>
            <input value={form.item_name} onChange={e => setForm({...form, item_name: e.target.value})} className="input-field w-full" required placeholder="e.g. Polo T-Shirt" /></div>
          <div><label className="block text-xs font-medium text-slate-600 mb-1">Size</label>
            <select value={form.size} onChange={e => setForm({...form, size: e.target.value})} className="input-field w-full">
              <option value="">Select</option>{['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', 'FREE_SIZE'].map(s => <option key={s} value={s}>{s}</option>)}</select></div>
          <div><label className="block text-xs font-medium text-slate-600 mb-1">Quantity</label>
            <input type="number" value={form.quantity} onChange={e => setForm({...form, quantity: +e.target.value})} className="input-field w-full" min="1" /></div>
          <div><label className="block text-xs font-medium text-slate-600 mb-1">Issue Date</label>
            <input type="date" value={form.issue_date} onChange={e => setForm({...form, issue_date: e.target.value})} className="input-field w-full" /></div>
          <div><label className="block text-xs font-medium text-slate-600 mb-1">Remarks</label>
            <input value={form.remarks} onChange={e => setForm({...form, remarks: e.target.value})} className="input-field w-full" /></div>
          <div className="flex items-end"><button type="submit" className="px-4 py-2 bg-emerald-600 text-white rounded-md text-sm font-medium" data-testid="submit-dress-btn">Issue</button></div>
        </form>
      )}
      {items.length === 0 ? <p className="text-sm text-slate-400 text-center py-4">No uniforms issued</p> : (
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-200">
            <th className="text-left py-2 text-xs text-slate-500">Date</th>
            <th className="text-left py-2 text-xs text-slate-500">Item</th>
            <th className="text-left py-2 text-xs text-slate-500">Size</th>
            <th className="text-center py-2 text-xs text-slate-500">Qty</th>
            <th className="text-left py-2 text-xs text-slate-500">Remarks</th>
          </tr></thead>
          <tbody>{items.map(i => (
            <tr key={i.id} className="border-b border-slate-50" data-testid={`dress-row-${i.id}`}>
              <td className="py-2 text-slate-500">{formatDate(i.issue_date)}</td>
              <td className="py-2 text-slate-900">{i.item_name}</td>
              <td className="py-2"><span className="text-xs px-1.5 py-0.5 rounded bg-slate-100">{i.size || '-'}</span></td>
              <td className="py-2 text-center">{i.quantity}</td>
              <td className="py-2 text-slate-500">{i.remarks || '-'}</td>
            </tr>
          ))}</tbody>
        </table>
      )}
    </div>
  );
}
